import { useState, useEffect } from 'react';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { Power, Loader2, ShieldAlert, CheckCircle2, AlertTriangle } from 'lucide-react';

export default function AdminSettings() {
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null); // 'success' | 'error'

  useEffect(() => {
    const ref = doc(db, 'settings', 'general');
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        setIsMaintenanceMode(snap.data().isMaintenanceMode ?? false);
      }
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, []);

  const handleToggle = async () => {
    setSaving(true);
    setFeedback(null);
    const newVal = !isMaintenanceMode;
    try {
      await setDoc(doc(db, 'settings', 'general'), { isMaintenanceMode: newVal }, { merge: true });
      setFeedback('success');
    } catch (err) {
      console.error('Error updating maintenance mode:', err);
      setFeedback('error');
    } finally {
      setSaving(false);
      setTimeout(() => setFeedback(null), 3000);
    }
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 max-w-2xl space-y-8">
      <div>
        <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white mb-2">إعدادات النظام</h1>
        <p className="text-lg text-slate-500 dark:text-zinc-400">التحكم في الإعدادات العامة للمنصة.</p>
      </div>

      {/* Maintenance Mode Card */}
      <div className={`bg-white dark:bg-zinc-900 border rounded-3xl p-8 shadow-sm transition-all duration-500 ${
        isMaintenanceMode
          ? 'border-amber-400/50 dark:border-amber-500/30 shadow-amber-500/10'
          : 'border-slate-200 dark:border-white/10'
      }`}>
        <div className="flex items-start gap-6">
          {/* Icon */}
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 transition-colors duration-500 ${
            isMaintenanceMode
              ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400'
              : 'bg-slate-50 dark:bg-white/5 text-slate-400 dark:text-zinc-500'
          }`}>
            <Power className="w-7 h-7" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h3 className="text-xl font-black text-slate-900 dark:text-white mb-1">وضع الصيانة</h3>
                <p className="text-slate-500 dark:text-zinc-400 font-medium text-sm leading-relaxed">
                  عند التفعيل، سيظهر لجميع الزوار (ما عدا الأدمن) شاشة الصيانة بدلاً من الموقع.
                </p>
              </div>

              {/* Toggle Switch */}
              <button
                id="maintenance-toggle"
                onClick={handleToggle}
                disabled={loading || saving}
                aria-pressed={isMaintenanceMode}
                className={`relative inline-flex shrink-0 h-8 w-16 items-center rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-zinc-900 ${
                  isMaintenanceMode
                    ? 'bg-amber-500 focus:ring-amber-500'
                    : 'bg-slate-200 dark:bg-zinc-700 focus:ring-primary-500'
                } ${loading || saving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:opacity-90'}`}
              >
                <span
                  className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-md transition-transform duration-300 ${
                    isMaintenanceMode ? 'translate-x-9' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* State Badge */}
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              {loading ? (
                <span className="flex items-center gap-2 text-sm text-slate-400 dark:text-zinc-500 font-bold">
                  <Loader2 className="w-4 h-4 animate-spin" /> جاري التحميل...
                </span>
              ) : (
                <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-bold ${
                  isMaintenanceMode
                    ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-500/20'
                    : 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20'
                }`}>
                  <span className={`w-2 h-2 rounded-full ${isMaintenanceMode ? 'bg-amber-500 animate-pulse' : 'bg-emerald-500'}`} />
                  {isMaintenanceMode ? 'الموقع في وضع الصيانة' : 'الموقع يعمل بشكل طبيعي'}
                </span>
              )}

              {saving && (
                <span className="flex items-center gap-2 text-sm text-slate-400 dark:text-zinc-500 font-bold">
                  <Loader2 className="w-4 h-4 animate-spin" /> جاري الحفظ...
                </span>
              )}

              {feedback === 'success' && (
                <span className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400 font-bold animate-in fade-in duration-300">
                  <CheckCircle2 className="w-4 h-4" /> تم الحفظ بنجاح
                </span>
              )}

              {feedback === 'error' && (
                <span className="flex items-center gap-2 text-sm text-rose-600 dark:text-rose-400 font-bold animate-in fade-in duration-300">
                  <AlertTriangle className="w-4 h-4" /> حدث خطأ، حاول مرة أخرى
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Warning block when active */}
        {isMaintenanceMode && (
          <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-2xl flex items-start gap-3 animate-in fade-in duration-300">
            <ShieldAlert className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <p className="text-sm font-bold text-amber-700 dark:text-amber-300 leading-relaxed">
              تنبيه: الموقع محجوب حالياً عن جميع المستخدمين ما عدا الأدمن. يمكنك التصفح بشكل طبيعي لاختبار التحديثات.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
